import {
  Hammer, CloudHail, Layers, Wrench, Home, ChevronRight,
  ShieldCheck, Snowflake, Sparkles, Clock, ShieldAlert, Droplets, Thermometer,
} from "lucide-react";
import { IMAGES, SERVICES, TRUST_POINTS } from "@/lib/site";

const SERVICE_ICONS = {
  roofing: Layers,
  storm: CloudHail,
  gutters: Wrench,
  siding: Home,
  soffit: Hammer,
};

const TRUST_ICONS = [
  ShieldCheck, Snowflake, ShieldAlert, Sparkles, Clock, Layers, Droplets, Thermometer,
];

export default function Services() {
  return (
    <>
      {/* ================== SERVICES — WHITE SECTION ================== */}
      <section
        id="services"
        data-testid="services-section"
        className="theme-light relative bg-white py-24 lg:py-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 lg:mb-12">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-[#D4A84B]" />
                <span className="es-label text-[#D4A84B]">What We Do</span>
              </div>
              <h2 className="font-serif-display text-[#101010] text-4xl sm:text-5xl leading-[1.05] tracking-tight">
                Roofing first.
                <br />
                Plus the full exterior.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-[#4B5055] text-base sm:text-lg leading-relaxed">
                We specialize in roofing, gutters, siding, soffit, fascia, and exterior
                storm restoration — the systems that work together to keep a Wisconsin
                home dry, warm, and sharp from the curb.
              </p>
            </div>
          </div>

          {/* 5 service cards — asymmetric 6-col tetris on large screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.key] || Hammer;
              const placement = i < 3 ? "lg:col-span-2" : "lg:col-span-3";
              return (
                <article
                  key={s.key}
                  data-testid={`service-card-${s.key}`}
                  className={`${placement} group relative bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D4A84B] transition-colors duration-500 overflow-hidden flex flex-col`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={s.image}
                      alt={`${s.title} — Everstone Roofing jobsite, Wisconsin`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 es-label text-[#D4A84B] bg-[#101010]/85 backdrop-blur-sm px-2.5 py-1 border border-[#D4A84B]/30">
                      0{i + 1}
                    </span>
                  </div>

                  <div className="p-7 lg:p-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className="w-5 h-5 text-[#D4A84B]" strokeWidth={1.5} />
                      <h3 className="font-serif-display text-[#101010] text-2xl leading-tight">
                        {s.title}
                      </h3>
                    </div>
                    <p className="text-[#4B5055] text-sm sm:text-base leading-relaxed mb-5">
                      {s.desc}
                    </p>
                    <ul className="space-y-2 mt-auto">
                      {s.points.map((p) => (
                        <li
                          key={p}
                          className="flex items-start gap-3 text-[#4B5055] text-sm"
                        >
                          <span className="mt-2 h-px w-3 bg-[#D4A84B] flex-shrink-0" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>

          <p
            data-testid="services-also-available"
            className="mt-8 text-[#6B7280] text-xs sm:text-sm tracking-wide"
          >
            Also available: window replacement, ventilation upgrades, and minor exterior
            carpentry — paired with most roofing or siding projects.
          </p>
        </div>
      </section>

      {/* ================== JOBSITE "OUR CREWS" BAND — DARK ================== */}
      <section
        data-testid="services-jobsite-band"
        className="relative bg-[#101010]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="relative overflow-hidden border border-[#2E3036] group min-h-[320px]">
            <img
              src={IMAGES.wideJobsite}
              alt="Everstone Roofing jobsite — dump trailer and crew working on a Wisconsin home"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#101010] via-[#101010]/70 to-transparent" />
            <div className="relative z-10 p-8 lg:p-14 max-w-xl">
              <span className="es-label text-[#D4A84B]">How We Work</span>
              <h3 className="font-serif-display text-white text-3xl sm:text-4xl mt-4 leading-tight">
                One point of contact. One standard on every roof.
              </h3>
              <p className="text-white/70 mt-4 text-sm sm:text-base">
                From the first inspection to the final walkthrough, you deal with
                Everstone directly. Every crew on our jobs is vetted, insured, and
                installs to the same GAF specifications — and we inspect the finished
                roof ourselves before we call it done.
              </p>
              <a
                href="#estimate"
                className="mt-6 inline-flex items-center gap-2 text-[#D4A84B] text-sm font-semibold group/cta"
                data-testid="services-cta"
              >
                Schedule an inspection
                <ChevronRight className="w-4 h-4 transition-transform group-hover/cta:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ================== TRUST HIGHLIGHTS — LIGHT GRAY ================== */}
      <section
        id="trust"
        data-testid="trust-section"
        className="theme-light relative bg-[#F4F5F6] py-20 lg:py-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-[#D4A84B]" />
                <span className="es-label text-[#D4A84B]">Why Everstone</span>
              </div>
              <h3 className="font-serif-display text-[#101010] text-3xl sm:text-4xl leading-tight tracking-tight">
                What you can count on,
                <br className="hidden sm:block" /> every job.
              </h3>
            </div>
            <p className="text-[#4B5055] text-sm sm:text-base max-w-md leading-relaxed">
              No gimmicks. No surprise fees. Just the basics done right — the way a
              Wisconsin contractor should.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
            {TRUST_POINTS.map((t, i) => {
              const Icon = TRUST_ICONS[i] || ShieldCheck;
              return (
                <div
                  key={t.label}
                  data-testid={`trust-point-${i}`}
                  className="bg-white p-7 lg:p-8 hover:bg-[#FAFBFC] transition-colors duration-500"
                >
                  {i === 0 ? (
                    <img
                      src={IMAGES.gafBadge}
                      alt="GAF Certified Residential Roofing Contractor"
                      data-testid="trust-gaf-badge"
                      className="w-16 h-auto mb-5"
                    />
                  ) : (
                    <Icon className="w-6 h-6 text-[#D4A84B] mb-5" strokeWidth={1.5} />
                  )}
                  <p className="text-[#101010] font-semibold text-base sm:text-lg leading-snug">
                    {t.label}
                  </p>
                  <p className="text-[#4B5055] text-sm mt-2 leading-relaxed">
                    {t.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
